
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    useWebPageVersions,
    useUpdateWebPageStatus,
    useDeleteWebPageContent,
    useCreateWebPageVersion
} from '@/lib/api/hooks/web-pages';
import { webPageService } from '@/lib/api/services/web-pages';
import { DocumentIcon, ReloadIcon } from '@/assets/icons';
import { Button, Loader, TanStackTable, type ColumnDef, Pagination } from '@/components/ui';
import Breadcrumb from "@/components/ui/Breadcrumb/Breadcrumb";
import CaretRightFilledIcon from "@/assets/icons/CaretRightFilledIcon";
import CaretLeftIcon from "@/assets/icons/CaretLeftIcon";
import formatDate from '@/utils/helpers/formatDate';
import { titleCase } from '@/utils/helpers/textTransform';
import { VersionSummary } from '@/lib/api/types';
import { ArrowDown, Crown, Search } from 'lucide-react';
import ActionDropdown from '@/components/ui/ActionDropdown';
import DeleteModal from '@/components/ui/DeleteModal';
import PublishModal from '@/components/ui/PublishModal';
import { showToast } from '@/lib/toast';
import WebPageVersionsStats from '@/pages/content/web-pages/WebPageVersionsStats';
import { cn } from '@/utils/helpers/classnames';

const PAGE_SIZE = 10;

const WebPageVersions: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [isCreatingDraft, setIsCreatingDraft] = useState(false);
    const [selectedContentId, setSelectedContentId] = useState<string | null>(null);

    const { data, isFetching, isError, refetch } = useWebPageVersions(id || '', { page: currentPage, size: PAGE_SIZE });


    const updateStatusMutation = useUpdateWebPageStatus();
    const deleteMutation = useDeleteWebPageContent();
    const createDraftMutation = useCreateWebPageVersion();

    const isInitialLoad = !data && isFetching;

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const versionInfo = data?.data?.data?.[0]; // Support case where first item contains nested summaries
    const pageName = versionInfo?.websitePageName || 'Page';
    const allVersions = useMemo(() => versionInfo?.versionSummaries || [], [versionInfo?.versionSummaries]);

    const filteredVersions = useMemo(() => {
        if (!searchQuery.trim()) return allVersions;
        const query = searchQuery.toLowerCase().trim();
        return allVersions.filter((v: VersionSummary) =>
            v.versionNumber.toLowerCase().includes(query) ||
            v.updatedByName?.toLowerCase().includes(query)
        );
    }, [allVersions, searchQuery]);

    const totalPages = Math.ceil(filteredVersions.length / PAGE_SIZE) || 1;

    const paginatedVersions = useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return filteredVersions.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredVersions, currentPage]);

    const handleVersionClick = useCallback((contentId: string) => {
        navigate(`edit/${contentId}`);
    }, [navigate]);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handlePublish = async () => {
        if (!selectedContentId) return;
        try {
            await updateStatusMutation.mutateAsync({
                contentId: selectedContentId,
                status: 'PUBLISH'
            });
            showToast.success('Version published successfully');
            setIsPublishModalOpen(false);
            refetch();
        } catch (error) {
            showToast.error('Failed to publish version');
            console.error(error);
        }
    };

    const handleDelete = async () => {
        if (!selectedContentId) return;
        try {
            await deleteMutation.mutateAsync(selectedContentId);
            showToast.success('Version deleted successfully');
            setIsDeleteModalOpen(false);
            refetch();
        } catch (error) {
            showToast.error('Failed to delete version');
            console.error(error);
        }
    };

    const handleCreateDraft = async () => {
        // Find published version, or just the most recent if none published
        const publishedVersion = allVersions.find(v => v.status === 'PUBLISH') || allVersions[0];
        if (!publishedVersion) {
            showToast.error('No version found to clone');
            return;
        }

        setIsCreatingDraft(true);
        try {
            // 1. Fetch full content of the version we want to clone
            const contentRes = await webPageService.getWebPageContent(publishedVersion.contentId);
            const sectionsMap = contentRes.data.content;

            // 2. Convert map to array for the API
            const sectionsArray = Object.values(sectionsMap);

            // 3. Create new draft version
            const response = await createDraftMutation.mutateAsync({
                websitePageId: id || '',
                data: {
                    status: 'DRAFT',
                    contents: sectionsArray
                }
            });

            // 4. Navigate to the new version in the editor
            if (response.success && response.data && typeof response.data === 'object' && 'id' in response.data) {
                const newContentId = response.data.id as string;
                showToast.success('Draft created successfully');
                navigate(`edit/${newContentId}`);
            } else {
                showToast.success('Draft created successfully');
                refetch();
            }
        } catch (error) {
            showToast.error('Failed to create draft');
            console.error(error);
        } finally {
            setIsCreatingDraft(false);
        }
    };

    const getInitials = (name: string | null): string => {
        if (!name) return '?';
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const columns = useMemo<ColumnDef<VersionSummary, unknown>[]>(() => [
        {
            id: 'version',
            header: 'Version Name',
            cell: ({ row }) => {
                const version = row.original;
                const isCurrent = version.status === 'PUBLISH';
                return (
                    <div className="flex items-center gap-3">
                        {isCurrent ? (
                            <Crown className="h-5 w-5 text-[#16A34A]" />
                        ) : (
                            <DocumentIcon className="" stroke="#6B7280" />
                        )}

                        <div className="flex flex-col">
                            <span className="text-base font-semibold leading-5 text-gray-900">{version.versionNumber}</span>
                            {isCurrent && (
                                <span className="inline-flex items-center gap-1 text-sm font-medium text-[#15803D]">
                                    <span className="h-2 w-2 rounded-full bg-[#16A34A]" />
                                    Current
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: ' whitespace-nowrap',
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const version = row.original;
                return (
                    <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            version.status === 'PUBLISH'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}
                    >
                        {version.status === 'PUBLISH' ? 'Published' : 'Draft'}
                    </span>
                );
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: ' whitespace-nowrap',
            },
        },
        {
            id: 'updatedAt',
            header: () => (
                <div className="inline-flex items-center gap-2">
                    <span>Last Updated</span>
                    <ArrowDown className="h-4 w-4" />
                </div>
            ),
            cell: ({ row }) => {
                const version = row.original;
                return <span className="text-sm font-medium text-gray-700">{formatDate(version.updatedAt, 'DD MMM YYYY, hh:mm A')}</span>;
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: ' whitespace-nowrap',
            },
        },
        {
            id: 'updatedBy',
            header: 'Updated by',
            cell: ({ row }) => {
                const version = row.original;
                return (
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-medium">
                            {getInitials(version.updatedByName)}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                            {version.updatedByName ? titleCase(version.updatedByName) : '-'}
                        </span>
                    </div>
                );
            },
            meta: {
                headerClassName: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                cellClassName: ' whitespace-nowrap',
            },
        },
        {
            id: 'actions',
            header: () => <span className="sr-only">Actions</span>,
            cell: ({ row }) => {
                const version = row.original;
                return (
                    <ActionDropdown
                        id={version.contentId}
                        actions={[
                            {
                                label: 'Edit',
                                onClick: () => handleVersionClick(version.contentId),
                            },
                            {
                                label: 'Publish',
                                onClick: () => {
                                    setSelectedContentId(version.contentId);
                                    setIsPublishModalOpen(true);
                                },
                                disabled: version.status === 'PUBLISH',
                            },
                            {
                                label: 'Delete',
                                onClick: () => {
                                    setSelectedContentId(version.contentId);
                                    setIsDeleteModalOpen(true);
                                },
                                variant: 'danger',
                            },
                        ]}
                    />
                );
            },
            meta: {
                headerClassName: 'relative px-6 py-3',
                cellClassName: 'whitespace-nowrap text-right text-sm font-medium',
            },
        },
    ], [handleVersionClick]);

    if (isInitialLoad) {
        return <Loader size="md" variant="wine" message="Loading versions..." />;
    }

    if (isError) {
        return (
            <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
                <p>Error loading page versions.</p>
                <Button onClick={() => refetch()} className="">Retry</Button>
            </div>
        );
    }

    const breadcrumbs = [
        { label: 'Website Content', href: '/admin/content' },
        { label: pageName, href: '#' },
        { label: 'Versions' }
    ];

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="bg-black p-4 md:px-8 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <Link to="/admin/content" className="flex items-center p-2 bg-[#333232] rounded cursor-pointer">
                        <CaretLeftIcon fill="white" />
                    </Link>
                    <Button onClick={() => refetch()} variant="ghost" className="text-white hover:text-black">
                        <ReloadIcon />
                        Reload
                    </Button>
                </div>
                <div className="flex gap-4 items-center">
                    <Button
                        variant="primary"
                        onClick={handleCreateDraft}
                        loading={isCreatingDraft}
                        disabled={isCreatingDraft}
                    >
                        Create draft
                    </Button>
                </div>
            </div>

            {/* Breadcrumbs */}
            <Breadcrumb
                separator={<CaretRightFilledIcon fill="#00764B" />}
                items={breadcrumbs}
                containerClassName="p-4 lg:px-12"
                linkClassName="!text-[#00764B] hover:underline"
            />

            {/* Content Area */}
            <div className="px-4 lg:px-12 pb-8 flex-1 flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold text-gray-900">{pageName}</h1>
                    <p className="text-sm text-gray-500">Manage page versions, draft and publishing history.</p>
                </div>

                {/* Web Page Stats */}
                {id && <WebPageVersionsStats pageId={id} />}

                <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col">
                    <div className="p-4 border-b border-gray-200 bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    

                        <div className="relative max-w-xs w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search versions..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#681628] focus:border-transparent outline-none"
                            />
                        </div>
                    </div>

                    <div >
                        <TanStackTable
                            data={paginatedVersions}
                            columns={columns}
                            getRowId={(row) => row.contentId}
                            onRowClick={(row) => handleVersionClick(row.contentId)}
                            rowClassName={(row) => cn(
                                'hover:bg-gray-50 transition-colors',
                                row.status === 'PUBLISH' && 'border-l-4 border-[#16A34A]'
                            )}
                            theadClassName="bg-gray-50"
                            emptyMessage="No versions found"
                        />
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-4">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <PublishModal
                isOpen={isPublishModalOpen}
                onClose={() => setIsPublishModalOpen(false)}
                onConfirm={handlePublish}
                isLoading={updateStatusMutation.isPending}
                itemType="Version"
            />

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                isLoading={deleteMutation.isPending}
                itemType="Version"
            />
        </div>
    );
};

export default WebPageVersions;
